// src/components/sales/pages/scheduled-messages/ScheduledMessagesPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import ScheduledMessagesView from "./ScheduledMessagesView";
import ScheduledMessageModal from "./ScheduledMessageModal";

import { scheduledMessagesService } from "../../../../services/scheduledMessages.service";
import { clientsService } from "../../../../services/clients.service";
import { salesTemplatesService } from "../../../../services/salesTemplates.service";

export default function ScheduledMessagesPage({ authData, logout }) {
  const [loading, setLoading] = useState(false);

  const [statusFilter, setStatusFilter] = useState("ALL");
  const [messages, setMessages] = useState([]);

  const [clients, setClients] = useState([]);
  const [templates, setTemplates] = useState([]);

  const [openModal, setOpenModal] = useState(false);
  const [mode, setMode] = useState("create"); // create | view
  const [viewing, setViewing] = useState(null);

  const unwrap = (res) => {
    if (res && typeof res === "object" && "data" in res) return res.data;
    return res;
  };

  // cancel gating (match view)
  const norm = (v) => String(v ?? "").trim().toLowerCase();
  const CANCELABLE = new Set(["pending", "scheduled", "queued"]);
  const canCancelRow = (row) => CANCELABLE.has(norm(row?.status ?? row?.state));

  const loadClients = async () => {
    const res = await clientsService.list({});
    const list = unwrap(res);
    setClients(Array.isArray(list) ? list : []);
  };

  const loadTemplates = async () => {
    const res = await salesTemplatesService.list({});
    const list = unwrap(res);
    setTemplates(Array.isArray(list) ? list : []);
  };

  const loadMessages = async (status = statusFilter) => {
    const res = await scheduledMessagesService.list({ status });
    const list = unwrap(res);
    setMessages(Array.isArray(list) ? list : []);
  };

  const refreshAll = async () => {
    setLoading(true);
    try {
      await Promise.all([loadClients(), loadTemplates(), loadMessages(statusFilter)]);
    } catch (e) {
      if (e?.message === "UNAUTHORIZED") {
        logout?.();
        return;
      }
      alert(e?.message || "Failed to load scheduled messages");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreate = () => {
    setMode("create");
    setViewing(null);
    setOpenModal(true);
  };

  const openView = async (row) => {
    setLoading(true);
    try {
      const id = row?.id ?? row?.scheduledMessageId ?? row?.messageId ?? row?.scheduledMessagesId;
      const res = await scheduledMessagesService.get(id);
      const full = unwrap(res) || row;
      setMode("view");
      setViewing(full);
      setOpenModal(true);
    } catch (e) {
      if (e?.message === "UNAUTHORIZED") {
        logout?.();
        return;
      }
      alert(e?.message || "Failed to load message details");
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => {
    setOpenModal(false);
    setViewing(null);
    setMode("create");
  };

  const createOne = async (payload) => {
    setLoading(true);
    try {
      await scheduledMessagesService.create(payload);
      await loadMessages(statusFilter);
      return true;
    } catch (e) {
      if (e?.message === "UNAUTHORIZED") {
        logout?.();
        return false;
      }
      alert(e?.message || "Create failed");
      return false;
    } finally {
      setLoading(false);
    }
  };

  const cancelOne = async (rowOrId) => {
    const row = typeof rowOrId === "object" ? rowOrId : null;

    // prevent cancel for non-cancelable statuses (avoid 400)
    if (row && !canCancelRow(row)) {
      alert("This message cannot be canceled unless it is Pending/Scheduled.");
      return;
    }

    const id =
      typeof rowOrId === "object"
        ? rowOrId?.id ?? rowOrId?.scheduledMessageId ?? rowOrId?.messageId ?? rowOrId?.scheduledMessagesId
        : rowOrId;

    if (!id) return;

    const ok = window.confirm("Are you sure you want to cancel this scheduled message?");
    if (!ok) return;

    setLoading(true);
    try {
      await scheduledMessagesService.cancel(id);
      await loadMessages(statusFilter);
    } catch (e) {
      if (e?.message === "UNAUTHORIZED") {
        logout?.();
        return;
      }
      alert(e?.message || "Cancel failed");
    } finally {
      setLoading(false);
    }
  };

  const clientMap = useMemo(() => {
    const m = new Map();
    (clients || []).forEach((c) => m.set(String(c.id), c));
    return m;
  }, [clients]);

  const templateMap = useMemo(() => {
    const m = new Map();
    (templates || []).forEach((t) => {
      const key = t?.id ?? t?.templateId ?? t?.salesTemplateId;
      if (key !== undefined && key !== null) m.set(String(key), t);
    });
    return m;
  }, [templates]);

  return (
    <>
      <ScheduledMessagesView
        authData={authData}
        logout={logout}
        loading={loading}
        messages={messages}
        clientMap={clientMap}
        templateMap={templateMap}
        clients={clients}
        templates={templates}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        onApplyFilter={async () => {
          setLoading(true);
          try {
            await loadMessages(statusFilter);
          } catch (e) {
            if (e?.message === "UNAUTHORIZED") {
              logout?.();
              return;
            }
            alert(e?.message || "Filter failed");
          } finally {
            setLoading(false);
          }
        }}
        onClearFilter={async () => {
          setStatusFilter("ALL");
          setLoading(true);
          try {
            await loadMessages("ALL");
          } catch (e) {
            if (e?.message === "UNAUTHORIZED") {
              logout?.();
              return;
            }
            alert(e?.message || "Failed");
          } finally {
            setLoading(false);
          }
        }}
        onRefresh={refreshAll}
        onCreateClick={openCreate}
        onView={openView}
        onCancel={cancelOne}
      />

      {openModal && (
        <ScheduledMessageModal
          mode={mode}
          initialData={viewing}
          clients={clients}
          templates={templates}
          loadingClients={loading && (!clients || clients.length === 0)}
          loadingTemplates={loading && (!templates || templates.length === 0)}
          onClose={closeModal}
          onSubmit={async (payload) => {
            const ok = await createOne(payload);
            if (ok) closeModal();
          }}
        />
      )}
    </>
  );
}
